import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { systemDocumentApi } from '../api/systemRegisterApi';
import { formatEnumLabel } from '../constants/enums';
import { apiErrorMessage } from '../utils/responseHelpers';
import BusinessCaseDialog, { emptyBusinessCase } from './BusinessCaseDialog';
import SystemDocumentDialog, { emptySystemDocumentRow } from './SystemDocumentDialog';

const BUSINESS_CASE_DOCUMENT_TYPE = 'BUSINESS_CASE';

const formatDisplayDate = (value) => {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
};

const preview = (value, max = 80) => {
  const text = String(value || '').trim();
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

/**
 * One-to-one Business Case register: table + add/edit dialog.
 * Eye icon opens the dedicated Business Case Form page (like Change Management view).
 * Attach Business Case Document opens the system document dialog (DocumentsOfSystem).
 */
export default function BusinessCaseTable({
  businessCase,
  onChange,
  error,
  softwareId = null,
  softwareName = '',
  onBeforeView,
  documents = [],
  onDocumentsChange,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [openingDocKey, setOpeningDocKey] = useState(null);

  const hasCase = Boolean(businessCase);

  const businessCaseDocuments = useMemo(
    () => (documents || []).filter((d) => d.documentType === BUSINESS_CASE_DOCUMENT_TYPE),
    [documents]
  );

  const openAdd = () => {
    setEditing(emptyBusinessCase());
    setDialogOpen(true);
  };

  const openEdit = () => {
    setEditing({ ...emptyBusinessCase(), ...(businessCase || {}) });
    setDialogOpen(true);
  };

  const openReport = () => {
    if (!businessCase) return;
    onBeforeView?.();
    navigate('/system-register/business-cases/view', {
      state: {
        businessCase,
        softwareName,
        softwareId: softwareId ?? null,
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  };

  const handleSave = (row) => {
    onChange(row);
    setDialogOpen(false);
    setEditing(null);
  };

  const confirmRemove = () => {
    onChange(null);
    setRemoveOpen(false);
  };

  const openAttachDocument = () => {
    setEditingDoc({
      ...emptySystemDocumentRow(),
      documentType: BUSINESS_CASE_DOCUMENT_TYPE,
      documentName: 'Business Case',
    });
    setDocDialogOpen(true);
  };

  const openEditDocument = (row) => {
    setEditingDoc({
      ...emptySystemDocumentRow(),
      ...row,
      documentType: BUSINESS_CASE_DOCUMENT_TYPE,
    });
    setDocDialogOpen(true);
  };

  const handleDocumentSave = (row) => {
    if (!onDocumentsChange) return;
    const nextRow = {
      ...row,
      documentType: BUSINESS_CASE_DOCUMENT_TYPE,
    };
    const list = [...(documents || [])];
    const idx = list.findIndex(
      (r) => r.key === nextRow.key || (nextRow.id != null && r.id === nextRow.id)
    );
    if (idx >= 0) {
      list[idx] = nextRow;
    } else {
      list.push(nextRow);
    }
    onDocumentsChange(list);
    setDocDialogOpen(false);
    setEditingDoc(null);
  };

  const removeDocument = (row) => {
    if (!onDocumentsChange || !row) return;
    onDocumentsChange(
      (documents || []).filter(
        (r) => r.key !== row.key && !(row.id != null && r.id === row.id)
      )
    );
  };

  const handleOpenDocument = async (row) => {
    if (!row?.location) {
      toast.error('No file attached to open.');
      return;
    }
    setOpeningDocKey(row.key || row.id);
    try {
      const response = await systemDocumentApi.fetchFile(row.location);
      const contentType =
        row.contentType
        || response.headers?.['content-type']
        || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.download = row.fileName || 'document';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to open document'));
    } finally {
      setOpeningDocKey(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
        <Typography variant="subtitle2" fontWeight={700}>
          Simple Business Case
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={openAdd}
            disabled={hasCase}
          >
            Add business case
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<AttachFileIcon />}
            onClick={openAttachDocument}
            disabled={!hasCase || !onDocumentsChange}
          >
            Attach Business Case Document
          </Button>
        </Box>
      </Box>

      {error ? (
        <Typography variant="caption" color="error" display="block" mb={1}>
          {error}
        </Typography>
      ) : null}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(primary, isDark ? 0.35 : 0.14),
          bgcolor: isDark ? 'background.paper' : alpha(primary, 0.02),
          overflowX: 'auto',
        }}
      >
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  fontWeight: 700,
                  bgcolor: alpha(primary, isDark ? 0.22 : 0.08),
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell>Date</TableCell>
              <TableCell>Submitted by</TableCell>
              <TableCell>Title / Role</TableCell>
              <TableCell>Project overview</TableCell>
              <TableCell>Proposed solution</TableCell>
              <TableCell
                align="right"
                sx={{
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  bgcolor: alpha(primary, isDark ? 0.22 : 0.08),
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasCase ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" py={1.5}>
                    No business case yet. Click “Add business case” to fill the template.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow hover>
                <TableCell>{formatDisplayDate(businessCase.caseDate)}</TableCell>
                <TableCell>{businessCase.submittedBy || '—'}</TableCell>
                <TableCell>{businessCase.titleRole || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>{preview(businessCase.projectOverview)}</TableCell>
                <TableCell sx={{ maxWidth: 220 }}>{preview(businessCase.proposedSolution)}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: 0,
                    zIndex: 1,
                    bgcolor: isDark ? 'background.paper' : '#fff',
                  }}
                >
                  <Tooltip title="View report">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={openReport}
                      aria-label="View business case report"
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={openEdit} aria-label="Edit business case">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setRemoveOpen(true)}
                      aria-label="Remove business case"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasCase ? (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
            Business case documents
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: alpha(primary, isDark ? 0.35 : 0.14),
              bgcolor: isDark ? 'background.paper' : alpha(primary, 0.02),
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      fontWeight: 700,
                      bgcolor: alpha(primary, isDark ? 0.22 : 0.08),
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableCell>Document name</TableCell>
                  <TableCell>Deployment status</TableCell>
                  <TableCell>Attached file</TableCell>
                  <TableCell align="right" width={96}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businessCaseDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" py={1.25}>
                        No business case document yet. Click “Attach Business Case Document”.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  businessCaseDocuments.map((row) => {
                    const opening = openingDocKey === (row.key || row.id);
                    return (
                      <TableRow key={row.key || row.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.documentName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.deploymentStatus ? formatEnumLabel(row.deploymentStatus) : '—'}
                        </TableCell>
                        <TableCell>
                          {row.fileName ? (
                            row.location ? (
                              <Link
                                component="button"
                                type="button"
                                variant="body2"
                                underline="hover"
                                disabled={opening}
                                onClick={() => handleOpenDocument(row)}
                                sx={{
                                  cursor: opening ? 'wait' : 'pointer',
                                  textAlign: 'left',
                                  border: 'none',
                                  background: 'none',
                                  p: 0,
                                  font: 'inherit',
                                }}
                              >
                                {opening ? 'Opening…' : row.fileName}
                              </Link>
                            ) : (
                              <Typography variant="body2">{row.fileName}</Typography>
                            )
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No file
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDocument(row)}
                              aria-label="Edit document"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeDocument(row)}
                              aria-label="Remove document"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      <BusinessCaseDialog
        open={dialogOpen}
        mode="edit"
        initialValues={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <SystemDocumentDialog
        open={docDialogOpen}
        initialValues={editingDoc}
        onClose={() => {
          setDocDialogOpen(false);
          setEditingDoc(null);
        }}
        onSave={handleDocumentSave}
      />

      <Dialog open={removeOpen} onClose={() => setRemoveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete business case?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the business case?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemove}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
